import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useDebounce } from '@/lib/useDebounce';
import { useLocaleFormatters } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/cn';
import { useDeletePrintDesign, usePrintDesigns } from './api';

const STATUS_STYLE: Record<string, string> = {
  draft: 'bg-surface-2 text-muted',
  rendered: 'bg-accent/15 text-accent-ink',
  final: 'bg-success/15 text-success',
};

const COVERAGE_KEY: Record<string, string> = {
  full: 'coverage.full',
  three_quarter: 'coverage.threeQuarter',
  half: 'coverage.half',
  spot: 'coverage.spot',
};

export function PrintWrapsPage() {
  const { t } = useTranslation('printWraps');
  const navigate = useNavigate();
  const { formatDate } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading } = usePrintDesigns(workspaceId, { search: debouncedSearch.length >= 2 ? debouncedSearch : undefined });
  const deleteDesign = useDeletePrintDesign(workspaceId);
  const { confirm, ConfirmDialog } = useConfirm();

  const items = data?.items ?? [];

  async function handleDelete(id: string, name: string) {
    const ok = await confirm({
      title: t('list.deleteConfirm.title', { name }),
      description: t('list.deleteConfirm.description'),
      confirmLabel: t('common:actions.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteDesign.mutateAsync(id);
      toast.success(t('list.toast.deleted'));
    } catch {
      toast.error(t('list.toast.deleteFailed'));
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <ConfirmDialog />
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h1 className="text-lg font-bold text-ink">{t('list.title')}</h1>
          <p className="text-sm text-muted">{t('list.subtitle')}</p>
        </div>
        <div className="relative ml-auto w-full sm:w-60">
          <Search size={14} strokeWidth={1.8} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input placeholder={t('list.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-8" />
          {search ? (
            <button type="button" onClick={() => setSearch('')} aria-label={t('list.clearSearch')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink">
              <X size={13} />
            </button>
          ) : null}
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => navigate('/print-wraps/new')}>
          <Plus size={14} /> {t('list.newDesign')}
        </Button>
      </div>

      {!isLoading && items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-dashed border-border px-4 py-14 text-center">
          <p className="text-sm text-muted">{t('list.empty')}</p>
          <Button size="sm" onClick={() => navigate('/print-wraps/new')}>
            <Plus size={14} className="mr-1" /> {t('list.newDesign')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((design) => (
            <div
              key={design.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/print-wraps/${design.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/print-wraps/${design.id}`);
                }
              }}
              className="flex cursor-pointer flex-col overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface text-left transition-colors hover:border-ink"
            >
              <div className="relative aspect-[4/3] max-h-48 w-full bg-surface-2">
                {design.latestRenderUrl || design.artworkUrl ? (
                  <img src={design.latestRenderUrl ?? design.artworkUrl ?? ''} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center text-xs text-muted">{t('list.noImage')}</div>
                )}
                <span className={cn('absolute right-2 top-2 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide', STATUS_STYLE[design.status])}>
                  {t(`status.${design.status}`)}
                </span>
              </div>
              <div className="flex flex-col gap-1 p-3">
                <span className="truncate text-sm font-semibold text-ink">{design.name}</span>
                <span className="truncate text-xs text-muted">{design.vehicleLabel}</span>
                <div className="flex items-center justify-between text-[11px] text-muted">
                  <span>{t(COVERAGE_KEY[design.coverageLevel] ?? 'coverage.full')}</span>
                  <span>{formatDate(new Date(design.updatedAt), 'MMM d')}</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDelete(design.id, design.name);
                  }}
                  className="mt-1 flex items-center gap-1 self-start text-[11px] font-semibold text-muted hover:text-danger"
                >
                  <Trash2 size={12} /> {t('common:actions.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
